<!-- eslint-disable max-len -->
<template>
  <div>
    <!-- One quiet step line -->
    <div class="ll-steps">
      <span class="ll-step ll-now">
        <span class="ll-n">1</span>
        {{ t('livvia.arrival.steps.s1') }}
        <span class="ll-time">· {{ t('livvia.arrival.steps.s1time') }}</span>
      </span>
      <span
        class="ll-arrow"
        aria-hidden="true"
      >→</span>
      <span class="ll-step">
        <span class="ll-n">2</span>
        {{ t('livvia.arrival.steps.s2') }}
      </span>
      <span
        class="ll-arrow"
        aria-hidden="true"
      >→</span>
      <span class="ll-step">
        <span class="ll-n">3</span>
        {{ t('livvia.arrival.steps.s3') }}
      </span>
    </div>

    <!-- Placeholder that IS the preview: ghosted report + lock overlay -->
    <div class="ll-ghost mt-5">
      <div
        class="ll-ghost-content"
        aria-hidden="true"
      >
        <div class="ll-ghost-head">
          <span class="ll-ghost-title">{{ t('livvia.arrival.ghost.title') }}</span>
          <span class="ll-ghost-pill"><span class="ll-ghost-dot" />{{ t('livvia.arrival.ghost.pill') }}</span>
        </div>
        <div class="ll-ghost-grid">
          <div class="ll-bars">
            <i style="height:30%" /><i style="height:48%" /><i style="height:38%" />
            <i style="height:62%" /><i
              class="ll-pk"
              :data-peak="t('livvia.arrival.ghost.peak')"
              style="height:100%"
            /><i style="height:70%" />
            <i style="height:44%" /><i style="height:56%" /><i style="height:32%" /><i style="height:24%" />
          </div>
          <div class="ll-rows">
            <div class="ll-row">
              <span><b>carla.brecho</b> · {{ t('home.landing.spots.report.m1') }}</span>
              <span class="ll-tag ll-tag-teal">{{ t('home.landing.spots.report.tagOrder') }}</span>
            </div>
            <div class="ll-row">
              <span><b>ju_semijoias</b> · {{ t('home.landing.spots.report.m2') }}</span>
              <span class="ll-tag ll-tag-amber">{{ t('home.landing.spots.report.tagDoubt') }}</span>
            </div>
            <div class="ll-row">
              <span><b>maria.moda</b> · {{ t('home.landing.spots.report.m3') }}</span>
              <span class="ll-tag ll-tag-teal">{{ t('home.landing.spots.report.tagOrder') }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="ll-lock">
        <span class="ll-lock-ic">
          <LockClosedIcon
            class="size-5 text-gray-500"
            aria-hidden="true"
          />
        </span>
        <b>{{ t('livvia.arrival.ghost.lockTitle') }}</b>
        <span>{{ t('livvia.arrival.ghost.lockSub') }}</span>
      </div>
    </div>

    <!-- One micro trust line -->
    <p class="ll-micro-trust">
      <b>{{ t('livvia.arrival.trustCount') }}</b> {{ t('livvia.arrival.trustRest') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { LockClosedIcon } from '@heroicons/vue/24/outline';

const { t } = useI18n();
</script>

<style scoped>
/* ---- step line ---- */
.ll-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: #9ca3af;
  font-weight: 500;
}
.ll-step {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  white-space: nowrap;
}
.ll-n {
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 0.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.66rem;
  font-weight: 700;
  background: #f3f4f6;
  color: #9ca3af;
  border: 1px solid #e5e7eb;
  flex: none;
}
.ll-step.ll-now {
  color: #374151;
  font-weight: 600;
}
.ll-step.ll-now .ll-n {
  background: #0d9488;
  color: #fff;
  border-color: #0d9488;
  animation: ll-ring-pulse 2.2s cubic-bezier(0.25, 1, 0.5, 1) infinite;
}
.ll-time {
  color: #9ca3af;
  font-weight: 500;
}
@keyframes ll-ring-pulse {
  0% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.35); }
  60% { box-shadow: 0 0 0 8px rgba(13, 148, 136, 0); }
  100% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0); }
}
.ll-arrow { color: #d1d5db; }

/* ---- ghosted preview ---- */
.ll-ghost {
  position: relative;
  border: 1.5px dashed #d1d5db;
  border-radius: 1rem;
  background: #fff;
  padding: 1.4rem 1.5rem;
  overflow: hidden;
}
.ll-ghost-content {
  opacity: 0.45;
  filter: saturate(0.75);
  pointer-events: none;
}
.ll-ghost-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.9rem;
}
.ll-ghost-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca3af;
}
.ll-ghost-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.62rem;
  font-weight: 700;
  color: #0f766e;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 999px;
  padding: 0.16rem 0.55rem;
}
.ll-ghost-dot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: #14b8a6;
}
.ll-ghost-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 1.4rem;
  align-items: end;
}
@media (max-width: 800px) {
  .ll-ghost-grid { grid-template-columns: 1fr; }
}
.ll-bars {
  display: flex;
  gap: 0.45rem;
  align-items: flex-end;
  height: 5.2rem;
  padding-top: 1.5rem;
}
.ll-bars i {
  flex: 1;
  border-radius: 0.3rem 0.3rem 0 0;
  background: #99f6e4;
  transform-origin: bottom;
  animation: ll-bar-breathe 3.4s ease-in-out infinite;
}
.ll-bars i.ll-pk {
  background: #0d9488;
  position: relative;
}
.ll-bars i.ll-pk::after {
  content: attr(data-peak);
  position: absolute;
  top: -1.4rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.58rem;
  font-weight: 700;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 0.08rem 0.45rem;
  white-space: nowrap;
}
@keyframes ll-bar-breathe {
  0%, 100% { transform: scaleY(0.92); }
  50% { transform: scaleY(1); }
}
.ll-rows {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.ll-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 0.6rem;
  padding: 0.45rem 0.6rem;
}
.ll-row b { color: #374151; }
.ll-tag {
  margin-left: auto;
  font-size: 0.56rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
  flex: none;
}
.ll-tag-teal { background: #f0fdfa; color: #0f766e; }
.ll-tag-amber { background: #fffbeb; color: #b45309; }

/* lock overlay */
.ll-lock {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  background:
    linear-gradient(
      180deg,
      rgba(249, 250, 251, 0.1),
      rgba(249, 250, 251, 0.72) 55%,
      rgba(249, 250, 251, 0.85)
    );
}
.ll-lock-ic {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 0.85rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 20px -8px rgba(17, 24, 39, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}
.ll-lock b {
  font-size: 0.95rem;
  color: #374151;
  font-weight: 700;
}
.ll-lock span {
  font-size: 0.78rem;
  color: #6b7280;
}

/* micro trust */
.ll-micro-trust {
  margin-top: 1.3rem;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 500;
  color: #9ca3af;
}
.ll-micro-trust b {
  color: #6b7280;
  font-weight: 600;
}

@media (prefers-reduced-motion: reduce) {
  .ll-bars i,
  .ll-step.ll-now .ll-n {
    animation: none !important;
  }
}
</style>
